/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Layout, TextLink } from '@oneaudi/unified-web-components';
import React from 'react';
import type { CardButtonsProps, I18nTexts } from '../../../@types/FeatureAppTypes';
import { useTrackingManager } from '../../context/useTrackingManager';
import { matchI18n, useI18n } from '../../i18n';
import type { StockCar } from './VtpPopover';
import VtpPopover from './VtpPopover';
import { useInitialState } from '../../context/InitialStateContext';

// eslint-disable-next-line react/display-name
const CardButtons = React.memo(
  ({ carlineGroup, carline, isPriceDisplayed, ctaDisplayOptions }: CardButtonsProps) => {
    const availableCarsInStock = carline.availableStock;

    let carsInStock = 0;
    if (availableCarsInStock?.newCars && availableCarsInStock?.newCars?.amount) {
      carsInStock += availableCarsInStock.newCars.amount;
    }
    if (availableCarsInStock?.usedCars && availableCarsInStock.usedCars?.amount) {
      carsInStock += availableCarsInStock.usedCars.amount;
    }

    const newCars = availableCarsInStock?.newCars as StockCar;
    const usedCars = availableCarsInStock?.usedCars as StockCar;

    const { discoverLinkUrl, configureLinkUrl } = carline;

    if (!discoverLinkUrl && !configureLinkUrl && carsInStock === 0) {
      return null;
    }
    const { i18nMessages } = useInitialState();

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { trackCTAClick } = useTrackingManager();
    const i18nButtonTexts: I18nTexts = {
      discoverCTALabel: useI18n('discoverCTALabel'),
      configureCTALabel: useI18n('configureCTALabel'),
    };
    const useTopPadding =
      !isPriceDisplayed && (discoverLinkUrl || configureLinkUrl || carsInStock > 0);

    return (
      <Layout
        direction="column"
        margin={[
          useTopPadding ? 'var(--spacing-relative-xxs)' : 'var(--spacing-relative-xs)',
          0,
          0,
          0,
        ]}
      >
        {discoverLinkUrl && (
          <TextLink
            data-testid="discoverButton"
            variant="standalone"
            size="sm"
            margin={[
              0,
              0,
              !configureLinkUrl && carsInStock === 0 ? 0 : 'var(--spacing-relative-sm)',
              0,
            ]}
            aria-label={matchI18n('discoverCTAAriaLabel', i18nMessages, {
              value: carline.name,
            })}
            href={discoverLinkUrl}
            onClick={() => {
              trackCTAClick(
                discoverLinkUrl,
                `${carlineGroup.id}~${carline.name}`,
                i18nButtonTexts.discoverCTALabel,
                'discover',
              );
            }}
          >
            {i18nButtonTexts.discoverCTALabel}
          </TextLink>
        )}
        {ctaDisplayOptions.showConfigureCTA && configureLinkUrl && (
          <TextLink
            data-testid="configureButton"
            variant="standalone"
            size="sm"
            href={configureLinkUrl}
            margin={[0, 0, carsInStock === 0 ? 0 : 'var(--spacing-relative-sm)', 0]}
            aria-label={matchI18n('configureCTAAriaLabel', i18nMessages, {
              value: carline.name,
            })}
            onClick={() => {
              trackCTAClick(
                configureLinkUrl,
                `${carlineGroup.id}~${carline.name}`,
                i18nButtonTexts.configureCTALabel,
                'configure',
              );
            }}
          >
            {i18nButtonTexts.configureCTALabel}
          </TextLink>
        )}
        {ctaDisplayOptions.showNewAndUsedCarsCTA && carsInStock > 0 && (
          <VtpPopover
            newCars={newCars}
            usedCars={usedCars}
            carsInStock={carsInStock}
            carlineGroup={carlineGroup.id}
            carline={carline.name}
          />
        )}
      </Layout>
    );
  },
);

export default CardButtons;
